import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash, ClipboardText, CalendarBlank, Eye, X, MagnifyingGlass, CheckCircle } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';

const MJESECI = ['Januar','Februar','Mart','April','Maj','Jun','Jul','Avgust','Septembar','Oktobar','Novembar','Decembar'];

function nextMonthLabel(mjesec) {
  const [mm, yyyy] = mjesec.split('/');
  let m = parseInt(mm, 10);
  let y = parseInt(yyyy, 10);
  m += 1;
  if (m > 12) { m = 1; y += 1; }
  return `${String(m).padStart(2,'0')}/${y}`;
}

export default function IOPPDPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newMjesec, setNewMjesec] = useState('');
  const [newGodina, setNewGodina] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState(null);  // {report}
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super_admin';

  const loadReports = async () => {
    setLoading(true);
    try {
      const r = await api.get('/ioppd/reports');
      setReports(r.data || []);
    } catch (e) {
      toast.error('Greska pri ucitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, []);

  const handleCreate = async () => {
    if (!newMjesec) return toast.error('Izaberite mjesec');
    const ym = `${String(newMjesec).padStart(2,'0')}/${newGodina}`;
    try {
      await api.post(`/ioppd/create?mjesec=${encodeURIComponent(ym)}`);
      toast.success(`Tabela IOPPD ${ym} kreirana`);
      setCreateOpen(false);
      setNewMjesec('');
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska pri kreiranju');
    }
  };

  const handleDelete = async (report, evt) => {
    evt?.stopPropagation?.();
    if (!await confirmDialog({ title: 'Brisanje tabele', message: `Obrisati IOPPD tabelu za ${report.mjesec}? Svi zapisi klijenata za ovaj mjesec ce biti obrisani.`, confirmText: 'Obrisi', variant: 'danger' })) return;
    try {
      await api.delete(`/ioppd/${report.id}`);
      toast.success('Tabela obrisana');
      loadReports();
    } catch {
      toast.error('Greska pri brisanju');
    }
  };

  const openDetails = async (report) => {
    setSelected(report);
    setEntriesLoading(true);
    setStatusFilter('all');
    setSearch('');
    try {
      const r = await api.get(`/ioppd/${report.id}/entries`);
      setEntries(r.data || []);
    } catch {
      toast.error('Greska pri ucitavanju zapisa');
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const closeDetails = () => {
    setSelected(null);
    setEntries([]);
    loadReports(); // refresh stats
  };

  const updateEntry = async (entryId, patch) => {
    try {
      const r = await api.put(`/ioppd/entry/${entryId}`, patch);
      setEntries(prev => prev.map(e => e.id === entryId ? r.data : e));
    } catch {
      toast.error('Greska pri azuriranju');
    }
  };

  const filteredEntries = useMemo(() => {
    let list = entries;
    if (statusFilter !== 'all') {
      if (statusFilter === 'bez_statusa') list = list.filter(e => !e.status);
      else list = list.filter(e => e.status === statusFilter);
    }
    const q = (search || '').toLowerCase().trim();
    if (q) list = list.filter(e => (e.naziv_firme || '').toLowerCase().includes(q) || (e.pib || '').toLowerCase().includes(q) || (e.napomena || '').toLowerCase().includes(q));
    return list;
  }, [entries, statusFilter, search]);

  const monthOptions = [
    { v: 1, l: 'Januar' }, { v: 2, l: 'Februar' }, { v: 3, l: 'Mart' }, { v: 4, l: 'April' },
    { v: 5, l: 'Maj' }, { v: 6, l: 'Jun' }, { v: 7, l: 'Jul' }, { v: 8, l: 'Avgust' },
    { v: 9, l: 'Septembar' }, { v: 10, l: 'Oktobar' }, { v: 11, l: 'Novembar' }, { v: 12, l: 'Decembar' },
  ];
  const yearOptions = [];
  const cy = new Date().getFullYear();
  for (let y = cy + 1; y >= cy - 3; y--) yearOptions.push(y);

  return (
    <div className="space-y-6" data-testid="ioppd-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-2xl font-semibold text-slate-900">Mjesečne IOPPD Tabele</h3>
          <p className="text-sm text-slate-500 mt-1">Automatski generirane tabele za praćenje predaje IOPPD prijava</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium" data-testid="ioppd-create-btn">
          <Plus size={18} className="mr-2" /> Kreiraj tabelu
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><p className="text-slate-500">Ucitavanje...</p></div>
      ) : reports.length === 0 ? (
        <Card className="p-8 text-center">
          <ClipboardText size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500 mb-2">Nema mjesečnih IOPPD tabela</p>
          <p className="text-xs text-slate-400">Kreirajte prvu tabelu klikom na dugme iznad</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map((r) => (
            <Card key={r.id} onClick={() => openDetails(r)} className="p-5 hover:shadow-md transition-shadow cursor-pointer" data-testid={`ioppd-card-${r.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                    <CalendarBlank size={20} weight="duotone" className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">IOPPD {r.mjesec}</h4>
                    <p className="text-xs text-slate-500">Rok: {r.rok_datum}</p>
                  </div>
                </div>
                <button onClick={(e) => handleDelete(r, e)} disabled={!isAdmin} className={`p-2 rounded transition-colors ${isAdmin ? 'hover:bg-red-100' : 'opacity-30 cursor-not-allowed'}`} title={isAdmin ? 'Obrisi tabelu' : 'Samo administrator moze brisati'} data-testid={`ioppd-delete-${r.id}`}>
                  <Trash size={16} className="text-red-600" />
                </button>
              </div>
              <div className="mb-3 pb-3 border-b border-slate-100">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Ukupno klijenata:</span>
                  <span className="font-semibold text-slate-900">{r.total}</span>
                </div>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-red-600">Nezavrseno:</span><span className="font-semibold text-red-600">{r.nezavrseno}</span></div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Bez statusa:</span><span className="text-slate-600">{r.bez_statusa}</span></div>
                  <div className="flex justify-between"><span className="text-amber-600">Primljeno:</span><span className="text-amber-600">{r.primljeno}</span></div>
                  <div className="flex justify-between"><span className="text-blue-600">U obradi:</span><span className="text-blue-600">{r.u_obradi}</span></div>
                  <div className="flex justify-between"><span className="text-slate-700">Unijeto:</span><span className="text-slate-700">{r.unijeto}</span></div>
                  <div className="flex justify-between col-span-2"><span className="text-green-600">Zavrseno:</span><span className="font-semibold text-green-600">{r.zavrseno}</span></div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-center gap-2 text-sm text-slate-700">
                <Eye size={16} /> Prikazi detalje
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Kreiraj IOPPD tabelu</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Mjesec *</Label>
              <Select value={newMjesec ? String(newMjesec) : ''} onValueChange={(v) => setNewMjesec(parseInt(v, 10))}>
                <SelectTrigger className="bg-white border-slate-300"><SelectValue placeholder="Izaberite mjesec" /></SelectTrigger>
                <SelectContent>{monthOptions.map(o => <SelectItem key={o.v} value={String(o.v)}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Godina *</Label>
              <Select value={String(newGodina)} onValueChange={(v) => setNewGodina(parseInt(v, 10))}>
                <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">Tabela obuhvata SVE aktivne klijente iz liste. Status "Nepredat" je pocetni; mijenja se rucno u detalju.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Otkazi</Button>
              <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700 text-white">Kreiraj</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Details sheet */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) closeDetails(); }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>IOPPD {selected?.mjesec} — detalji</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Pretraga po klijentu, PIB-u, napomeni..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white border-slate-300" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-white border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Svi statusi</SelectItem>
                <SelectItem value="bez_statusa">Bez statusa</SelectItem>
                <SelectItem value="primljeno">Primljeno</SelectItem>
                <SelectItem value="u_obradi">U obradi</SelectItem>
                <SelectItem value="unijeto">Unijeto</SelectItem>
                <SelectItem value="zavrseno">Zavrseno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left text-xs font-semibold tracking-wider uppercase text-slate-600 border-b border-slate-200">
                  <th className="px-3 py-2 w-12">R.br</th>
                  <th className="px-3 py-2">Klijent</th>
                  <th className="px-3 py-2 w-28">PIB</th>
                  <th className="px-3 py-2 w-44">Status</th>
                  <th className="px-3 py-2">Napomena</th>
                </tr>
              </thead>
              <tbody>
                {entriesLoading && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Ucitavanje...</td></tr>}
                {!entriesLoading && filteredEntries.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Nema zapisa</td></tr>}
                {filteredEntries.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-500">{e.redni_broj}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      {e.status === 'zavrseno' && <CheckCircle size={14} weight="fill" className="inline text-green-600 mr-1" />}
                      {e.naziv_firme}
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{e.pib || '-'}</td>
                    <td className="px-3 py-2">
                      <Select value={e.status || 'none'} onValueChange={(v) => updateEntry(e.id, { status: v === 'none' ? '' : v })}>
                        <SelectTrigger className="h-8 border-slate-300 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Bez statusa</SelectItem>
                          <SelectItem value="primljeno">Primljeno</SelectItem>
                          <SelectItem value="u_obradi">U obradi</SelectItem>
                          <SelectItem value="unijeto">Unijeto</SelectItem>
                          <SelectItem value="zavrseno">Zavrseno</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      <Input value={e.napomena || ''} onChange={(evt) => {
                        const v = evt.target.value;
                        setEntries(prev => prev.map(x => x.id === e.id ? { ...x, napomena: v } : x));
                      }} onBlur={(evt) => updateEntry(e.id, { napomena: evt.target.value })} placeholder="Napomena..." className="h-8 text-xs border-slate-300" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end pt-3">
            <Button variant="outline" onClick={closeDetails}><X size={14} className="mr-2" /> Zatvori</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
